import { MRT_ColumnDef, MRT_RowData } from 'material-react-table';
import { defineMessage, IntlFormatters, MessageDescriptor } from 'react-intl';

/** Shared table column header for row action columns. */
export const actionsColumnHeaderMessage = defineMessage({
  id: 'common.text.actions',
  defaultMessage: 'Acciones',
});

/**
 * Only set `align` — do not pass a column-level `sx`.
 * MRT shallow-merges column `muiTableHeadCellProps` over the table defaults, so a
 * column `sx` would replace MrtTable's header typography (`fontWeight: 500`) and
 * leave MRT's default `fontWeight: 'bold'` on this header only.
 * With `layoutMode: 'grid'`, MRT already maps `align` to `justifyContent`.
 */
const centeredCellProps = {
  align: 'center' as const,
};

/** Compact width that fits 2–3 icon buttons without growing like a data column. */
const DEFAULT_ACTIONS_COLUMN_SIZE = 120;

export interface CreateActionsColumnOptions<T extends MRT_RowData> {
  /** Renders the action buttons for each row. */
  Cell: NonNullable<MRT_ColumnDef<T>['Cell']>;
  /** Override the default "Acciones" header. */
  header?: string | MessageDescriptor;
  /** Override the default compact width. Use a larger value for 4+ icons or labeled buttons. */
  size?: number;
}

/**
 * Builds a reusable MRT actions column with centered header and cell content.
 * Header defaults to `common.text.actions` ("Acciones").
 * Stays compact (`grow: false`) so leftover table width goes to data columns.
 */
export function createActionsColumn<T extends MRT_RowData>(
  $t: IntlFormatters['$t'],
  options: CreateActionsColumnOptions<T>,
): MRT_ColumnDef<T> {
  const { Cell, header, size = DEFAULT_ACTIONS_COLUMN_SIZE } = options;

  const resolvedHeader =
    header == null
      ? $t(actionsColumnHeaderMessage)
      : typeof header === 'string'
        ? header
        : $t(header);

  return {
    id: 'actions',
    enableSorting: false,
    accessorFn: () => '',
    header: resolvedHeader,
    size,
    grow: false,
    muiTableHeadCellProps: centeredCellProps,
    muiTableBodyCellProps: centeredCellProps,
    Cell,
  };
}
